import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Loader2, Sparkles, Building2, Palette, Users, Layers, X, Upload, Search,
} from "lucide-react";
import Logo from "../components/marketing/Logo";
import api, { formatApiError } from "../lib/api";
import { track, EVT } from "../lib/analytics";
import { useAuth } from "../context/AuthContext";

function CompaniesHouseSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const search = async () => {
    if (q.length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/companies-house/search?q=${encodeURIComponent(q)}`);
      setResults(data.items || []);
    } catch (e) { setResults([]); }
    finally { setLoading(false); }
  };
  const pick = async (item) => {
    setPicked(item);
    try {
      const { data } = await api.get(`/api/companies-house/company/${item.company_number}`);
      onPick({ ...item, ...data.company });
      await api.post("/api/companies-house/adopt", {
        company_number: item.company_number,
        company_name: item.title || item.company_name,
      });
    } catch (e) { /* ignore */ }
  };
  return (
    <div className="mb-6 p-4 rounded-lg bg-indigo2/5 border border-indigo2/20" data-testid="ch-search">
      <div className="t-label text-indigo2 mb-2">Verify with Companies House (recommended)</div>
      <div className="flex gap-2">
        <input className="input2 flex-1" placeholder="Search company name or number" value={q}
               onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter" && search()} data-testid="ch-input" />
        <button onClick={search} disabled={loading || q.length < 2} className="btn btn-secondary" data-testid="ch-search-btn">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>}Search
        </button>
      </div>
      {results && results.length > 0 && !picked && (
        <div className="mt-3 max-h-56 overflow-y-auto bg-white rounded border border-border2 divide-y divide-border2">
          {results.slice(0, 8).map((r) => (
            <button key={r.company_number} onClick={() => pick(r)} className="block w-full text-left px-3 py-2 hover:bg-bg2" data-testid={`ch-result-${r.company_number}`}>
              <div className="t-body-sm font-medium">{r.title}</div>
              <div className="t-caption text-slate2">{r.company_number} · {r.company_status} · {r.address_snippet || ""}</div>
            </button>
          ))}
        </div>
      )}
      {results && results.length === 0 && <div className="t-caption mt-2">No matches.</div>}
      {picked && (
        <div className="mt-3 p-2 rounded bg-emerald2/10 t-body-sm text-emerald2-dark flex items-center gap-2" data-testid="ch-picked">
          <CheckCircle2 size={14} /> Verified: <strong>{picked.title || picked.company_name}</strong> ({picked.company_number})
        </div>
      )}
    </div>
  );
}

const STEPS = ["Welcome", "Firm profile", "Branding", "Team", "First flow"];

function ProgressBar({ step }) {
  return (
    <div className="w-full px-6 py-4 border-b border-border2 bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <Logo variant="dark" size="sm" />
          <span className="t-caption">Step {step} of {STEPS.length}</span>
        </div>
        <div className="flex gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full transition-colors duration-500" style={{ background: i < step ? "#4F46E5" : "#E2E8F0" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Confetti() {
  const pieces = Array.from({ length: 60 });
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" aria-hidden="true">
      {pieces.map((_, i) => {
        const colors = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#0F1C2E"];
        const c = colors[i % colors.length];
        const left = Math.random() * 100;
        const delay = Math.random() * 1.5;
        const size = 6 + Math.random() * 6;
        return (
          <span
            key={i}
            style={{
              position: "absolute", top: -20, left: `${left}%`,
              width: size, height: size, background: c,
              borderRadius: i % 3 === 0 ? "50%" : 2,
              animation: `confetti-fall 2.4s ${delay}s ease-out forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0); opacity: 1; }
          100% { transform: translateY(110vh) rotate(${Math.random() * 720}deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { span { animation: none !important; } }
      `}</style>
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [showConfetti, setShowConfetti] = useState(false);
  const [done, setDone] = useState(false);

  // Step 2 profile
  const [profile, setProfile] = useState({
    fca_number: "", address_line1: "", address_line2: "", town: "", postcode: "", website: "",
    compliance_contact_name: "", compliance_contact_email: "", compliance_contact_phone: "",
  });
  // Step 3 branding
  const [branding, setBranding] = useState({ primary_color: "#4F46E5", logo_data_url: "" });
  // Step 4 team
  const [invites, setInvites] = useState([{ email: "", role: "advisor" }]);
  // Step 5 flow
  const [savingFlow, setSavingFlow] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [w, f] = await Promise.all([api.get("/api/firm/wizard"), api.get("/api/firm/me")]);
        if (w.data.completed) { nav("/app/dashboard"); return; }
        setStep(w.data.current_step || 1);
        if (f.data.fca_number) setProfile((p) => ({ ...p, fca_number: f.data.fca_number, ...f.data }));
        if (f.data.branding?.primary_color) setBranding(f.data.branding);
      } catch {}
    })();
  }, [nav]);

  const persistStep = async (s) => {
    try { await api.patch("/api/firm/wizard", { current_step: s }); } catch {}
  };

  useEffect(() => { if (step === 1) setShowConfetti(true); }, [step]);

  const next = async () => {
    const ns = step + 1;
    setStep(ns); await persistStep(ns);
  };

  const skip = async () => next();

  const finish = async () => {
    await api.patch("/api/firm/wizard", { completed: true, current_step: STEPS.length });
    track(EVT.firm_onboarding_completed);
    setDone(true); setShowConfetti(true);
    setTimeout(() => { refresh(); nav("/app/dashboard"); }, 2800);
  };

  const onSaveProfile = async () => {
    try { await api.patch("/api/firm/profile", profile); await next(); } catch (e) { alert(formatApiError(e)); }
  };

  const onSaveBranding = async () => {
    try { await api.patch("/api/firm/branding", branding); await next(); } catch (e) { alert(formatApiError(e)); }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Max 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, logo_data_url: reader.result });
    reader.readAsDataURL(file);
  };

  const sendInvites = async () => {
    for (const inv of invites) {
      if (inv.email && inv.role) {
        await api.post("/api/firm/team/invite", inv).catch(() => {});
        track(EVT.team_member_invited, { role: inv.role, source: "wizard" });
      }
    }
    await next();
  };

  const createFromTemplate = async (template) => {
    setSavingFlow(true);
    try {
      const { data } = await api.post("/api/flows", { name: `My first ${template === "ifa" ? "IFA" : template === "mortgage" ? "Mortgage" : "Document"} onboarding`, template });
      await finish();
      setTimeout(() => nav(`/app/flows/${data.id}/edit`), 2500);
    } catch (e) {
      alert(formatApiError(e));
      setSavingFlow(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-6">
        <Confetti />
        <div className="card2 p-12 max-w-lg text-center animate-slide-up">
          <div className="w-20 h-20 rounded-full bg-emerald2-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={44} className="text-emerald2-dark" />
          </div>
          <h1 className="t-display mb-3">You're ready to go, {user?.first_name}!</h1>
          <p className="t-body-lg text-slate2 mb-2">Redirecting to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <ProgressBar step={step} />
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        {showConfetti && step === 1 && <Confetti />}
        <div className="w-full max-w-2xl">
          {step === 1 && (
            <div className="card2 p-10 text-center animate-slide-up" data-testid="wizard-step-1">
              <div className="w-16 h-16 rounded-full bg-indigo2 flex items-center justify-center mx-auto mb-5">
                <Sparkles size={28} className="text-white" />
              </div>
              <h1 className="t-display mb-3">Welcome to Compliche, {user?.first_name}</h1>
              <p className="t-body-lg text-slate2 mb-8 max-w-md mx-auto">
                Let's get you set up in about 4 minutes so you can start your first compliant onboarding.
              </p>
              <button className="btn btn-primary btn-lg" onClick={next} data-testid="wizard-start">Let's get started <ArrowRight size={18} /></button>
            </div>
          )}

          {step === 2 && (
            <div className="card2 p-10 animate-slide-up" data-testid="wizard-step-2">
              <Building2 size={28} className="text-indigo2 mb-3" />
              <h1 className="t-h1 mb-2">Tell us about your firm</h1>
              <p className="t-body text-slate2 mb-6">This information appears on your firm's audit trail and in regulatory exports.</p>

              <CompaniesHouseSearch onPick={(co) => setProfile((p) => ({
                ...p,
                address_line1: co.registered_office_address?.address_line_1 || p.address_line1,
                address_line2: co.registered_office_address?.address_line_2 || p.address_line2,
                town: co.registered_office_address?.locality || p.town,
                postcode: co.registered_office_address?.postal_code || p.postcode,
                companies_house_number: co.company_number,
              }))} />

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-1"><label className="label2">FCA number<span className="req">*</span></label><input className="input2 t-mono" value={profile.fca_number} onChange={(e) => setProfile({ ...profile, fca_number: e.target.value })} data-testid="wiz-fca" /></div>
                <div className="col-span-1"><label className="label2">Website</label><input className="input2" placeholder="https://" value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })} /></div>
                <div className="col-span-2"><label className="label2">Address line 1</label><input className="input2" value={profile.address_line1 || ""} onChange={(e) => setProfile({ ...profile, address_line1: e.target.value })} data-testid="wiz-addr1" /></div>
                <div className="col-span-2"><label className="label2">Address line 2</label><input className="input2" value={profile.address_line2 || ""} onChange={(e) => setProfile({ ...profile, address_line2: e.target.value })} /></div>
                <div className="col-span-1"><label className="label2">Town</label><input className="input2" value={profile.town || ""} onChange={(e) => setProfile({ ...profile, town: e.target.value })} /></div>
                <div className="col-span-1"><label className="label2">Postcode</label><input className="input2 t-mono" value={profile.postcode || ""} onChange={(e) => setProfile({ ...profile, postcode: e.target.value })} /></div>
              </div>

              <div className="t-label text-slate2 mt-8 mb-3">Compliance contact</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="label2">Name</label><input className="input2" value={profile.compliance_contact_name || ""} onChange={(e) => setProfile({ ...profile, compliance_contact_name: e.target.value })} /></div>
                <div><label className="label2">Email</label><input type="email" className="input2" value={profile.compliance_contact_email || ""} onChange={(e) => setProfile({ ...profile, compliance_contact_email: e.target.value })} /></div>
                <div><label className="label2">Phone</label><input type="tel" className="input2" value={profile.compliance_contact_phone || ""} onChange={(e) => setProfile({ ...profile, compliance_contact_phone: e.target.value })} /></div>
              </div>

              <div className="mt-8 flex justify-between"><button className="btn btn-ghost" onClick={skip}>Skip for now</button><button className="btn btn-primary" onClick={onSaveProfile} data-testid="wiz-save-profile">Continue <ArrowRight size={16} /></button></div>
            </div>
          )}

          {step === 3 && (
            <div className="card2 p-10 animate-slide-up" data-testid="wizard-step-3">
              <Palette size={28} className="text-indigo2 mb-3" />
              <h1 className="t-h1 mb-2">Make it yours</h1>
              <p className="t-body text-slate2 mb-6">Your clients will see this on every screen.</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="label2 mb-2">Firm logo (PNG/SVG, max 2MB)</label>
                  <label className="block border-2 border-dashed border-border2 rounded-lg p-6 text-center cursor-pointer hover:border-indigo2 transition-colors">
                    {branding.logo_data_url ? (
                      <img src={branding.logo_data_url} alt="logo" className="max-h-16 mx-auto" />
                    ) : (
                      <><Upload size={22} className="mx-auto mb-2 text-slate2" /><span className="t-body-sm">Drop or click to upload</span></>
                    )}
                    <input type="file" accept="image/png,image/svg+xml,image/jpeg" className="hidden" onChange={onLogoFile} data-testid="wiz-logo" />
                  </label>

                  <label className="label2 mt-5">Primary brand color</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" className="w-12 h-11 rounded-md border border-border2 cursor-pointer" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} data-testid="wiz-color" />
                    <input type="text" className="input2 t-mono" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
                  </div>
                </div>

                <div>
                  <div className="t-label text-slate2 mb-2">Client portal preview</div>
                  <div className="border border-border2 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 flex items-center gap-3" style={{ background: branding.primary_color, color: "#fff" }}>
                      {branding.logo_data_url ? <img src={branding.logo_data_url} className="h-7" alt="" /> : <div className="t-h4">{user?.firm_name}</div>}
                    </div>
                    <div className="p-5 bg-surface">
                      <div className="t-h4">Welcome, [Client]</div>
                      <p className="t-body-sm text-slate2">Sample preview of how your branded portal will appear.</p>
                      <div className="btn mt-3" style={{ background: branding.primary_color, color: "#fff" }}>Begin onboarding</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between"><button className="btn btn-ghost" onClick={skip}>Skip for now</button><button className="btn btn-primary" onClick={onSaveBranding} data-testid="wiz-save-branding">Continue <ArrowRight size={16} /></button></div>
            </div>
          )}

          {step === 4 && (
            <div className="card2 p-10 animate-slide-up" data-testid="wizard-step-4">
              <Users size={28} className="text-indigo2 mb-3" />
              <h1 className="t-h1 mb-2">Who else works with you?</h1>
              <p className="t-body text-slate2 mb-6">Invite team members. They'll get a secure setup link by email.</p>

              <div className="space-y-3">
                {invites.map((inv, i) => (
                  <div key={i} className="grid grid-cols-[1fr_180px_40px] gap-2">
                    <input type="email" placeholder="colleague@firm.co.uk" className="input2" value={inv.email} onChange={(e) => { const c = [...invites]; c[i].email = e.target.value; setInvites(c); }} data-testid={`invite-email-${i}`} />
                    <select className="input2" value={inv.role} onChange={(e) => { const c = [...invites]; c[i].role = e.target.value; setInvites(c); }} data-testid={`invite-role-${i}`}>
                      <option value="advisor">Advisor</option>
                      <option value="compliance_manager">Compliance Manager</option>
                      <option value="read_only">Read-only</option>
                    </select>
                    <button onClick={() => setInvites(invites.filter((_, j) => j !== i))} className="text-slate2 hover:text-rose2" aria-label="Remove"><X size={18} /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => setInvites([...invites, { email: "", role: "advisor" }])} className="t-body-sm text-indigo2 hover:underline mt-3">+ Add another</button>

              <div className="mt-8 flex justify-between"><button className="btn btn-ghost" onClick={skip}>I'll do this later</button><button className="btn btn-primary" onClick={sendInvites} data-testid="wiz-send-invites">Send invitations <ArrowRight size={16} /></button></div>
            </div>
          )}

          {step === 5 && (
            <div className="card2 p-10 animate-slide-up" data-testid="wizard-step-5">
              <Layers size={28} className="text-indigo2 mb-3" />
              <h1 className="t-h1 mb-2">Create your first onboarding flow</h1>
              <p className="t-body text-slate2 mb-6">Get a head start with a regulated template, or build from scratch.</p>

              <div className="space-y-3">
                <div className="card2 card2-hover p-5 cursor-pointer flex items-center justify-between" onClick={() => createFromTemplate("ifa")} data-testid="template-ifa">
                  <div>
                    <div className="badge2 badge-locked mb-2">Recommended</div>
                    <h3 className="t-h4">New Client Onboarding (IFA)</h3>
                    <p className="t-body-sm text-slate2 mt-1">5 steps — KYC, ID + address, consent, signature</p>
                  </div>
                  <ArrowRight size={20} className="text-slate2" />
                </div>
                <div className="card2 card2-hover p-5 cursor-pointer flex items-center justify-between" onClick={() => createFromTemplate("mortgage")} data-testid="template-mortgage">
                  <div>
                    <h3 className="t-h4">New Client Onboarding (Mortgage)</h3>
                    <p className="t-body-sm text-slate2 mt-1">3 steps — borrower details, payslips, P60</p>
                  </div>
                  <ArrowRight size={20} className="text-slate2" />
                </div>
                <div className="card2 card2-hover p-5 cursor-pointer flex items-center justify-between" onClick={() => createFromTemplate("doc_collection")} data-testid="template-docs">
                  <div>
                    <h3 className="t-h4">Document Collection</h3>
                    <p className="t-body-sm text-slate2 mt-1">2 steps — welcome + custom uploads</p>
                  </div>
                  <ArrowRight size={20} className="text-slate2" />
                </div>
              </div>

              <div className="mt-6 flex justify-between items-center">
                <button className="t-body-sm text-slate2 hover:text-ink" onClick={finish} data-testid="wiz-do-later">I'll do this later →</button>
                {savingFlow && <Loader2 size={20} className="animate-spin text-indigo2" />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
